import { useQueryClient } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useCurrentStateAndParams } from '@uirouter/react';
import { Row } from '@tanstack/react-table';
import clsx from 'clsx';

import { withLimitToBE } from '@/react/hooks/useLimitToBE';
import { queryKeys } from '@/react/portainer/environments/update-schedules/queries/query-keys';
import { createStore } from '@/react/portainer/environments/update-schedules/DetailsView/store';
import { useEnvironmentList } from '@/react/portainer/environments/queries';
import { useParamState } from '@/react/hooks/useParamState';
import {
  EdgeUpdateListItemResponse,
  useItem,
} from '@/react/portainer/environments/update-schedules/queries/useItem';
import { EdgeGroupLabels } from '@/react/portainer/environments/update-schedules/common/EdgeGroupLabels';
import { StatusType } from '@/react/edge/edge-stacks/types';
import { formatDate } from '@/react/portainer/environments/update-schedules/common/utils';

import { Datatable } from '@@/datatables';
import { PageHeader } from '@@/PageHeader';
import { useTableState } from '@@/datatables/useTableState';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { DefaultType } from '@@/datatables/types';

import { getColumns } from './columns';
import { ScheduleEnvironment } from './types';
import { TableRow } from './TableRow';
import { EnvironmentDetailsWidget } from './EnvironmentDetailsWidget';

const tableKey = 'update-schedules-environments';
const settingsStore = createStore(tableKey);

export default withLimitToBE(DetailsView);

function DetailsView() {
  const queryClient = useQueryClient();

  const {
    params: { id: idParam },
  } = useCurrentStateAndParams();

  const scheduleId = parseInt(idParam, 10);
  const schedule = useItem(scheduleId, {
    refetchInterval(data) {
      if (!data) {
        return 0;
      }

      return Object.values(data.edgeStack?.Status ?? []).some((status) =>
        status.Status.every((s) => s.Type === StatusType.Running)
      )
        ? 0
        : 10000;
    },
  });

  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useParamState<StatusType | undefined>(
    'status',
    (value) => (value !== undefined ? parseInt(value, 10) : undefined)
  );

  const [highlightedItem, setHighlightedItem] = useState(0);

  const tableState = useTableState(settingsStore, tableKey);

  const environmentsQuery = useEnvironmentList({
    pageLimit: tableState.pageSize,
    page: page + 1,
    search: tableState.search,
    sort: tableState.sortBy?.id as 'Group' | 'Name',
    order: tableState.sortBy?.desc ? 'desc' : 'asc',
    edgeStackId: schedule.data?.edgeStackId,
    edgeStackStatus: statusFilter,
  });

  const envStatusSelectOptions = [
    { value: StatusType.Error, label: 'Failed' },
    { value: StatusType.RemoteUpdateSuccess, label: 'Completed' },
  ];

  const scheduleEnvironments: ScheduleEnvironment[] =
    environmentsQuery.environments.map((env) => ({
      ...(schedule.data as EdgeUpdateListItemResponse),
      environment: env,
    }));

  useEffect(() => {
    if (
      scheduleEnvironments.length > 0 &&
      !scheduleEnvironments.some(
        (env) => env.environment.Id === highlightedItem
      )
    ) {
      setHighlightedItem(scheduleEnvironments[0].environment.Id);
    }
  }, [scheduleEnvironments, highlightedItem]);

  return (
    <div className="row">
      <PageHeader
        title={schedule.data?.name ?? ''}
        breadcrumbs={[
          {
            link: 'portainer.endpoints.updateSchedules',
            label: 'Update & Rollback',
          },
          {
            link: 'portainer.endpoints.updateSchedules.details',
            label: schedule.data?.name ?? '',
          },
        ]}
        reload
        onReload={() => queryClient.invalidateQueries(queryKeys.base())}
      />
      <div className="flex">
        <div className="w-1/2">
          <div className="[&_thead]:hidden [&_table]:!mb-1.5">
            <Datatable
              title="Environments"
              titleIcon={Activity}
              dataset={scheduleEnvironments ?? []}
              columns={getColumns(setHighlightedItem)}
              getRowId={(row) => String(row.environment.Id)}
              renderRow={renderRow}
              highlightedItemId={String(highlightedItem)}
              settingsManager={tableState}
              data-cy="test-test"
              disableSelect
              isLoading={environmentsQuery.isLoading}
              includeSearch={false}
              isServerSidePagination
              page={page}
              onPageChange={setPage}
              totalCount={environmentsQuery.totalCount}
              description={
                <div className="flex flex-col gap-3">
                  <div className="flex flex-row gap-3">
                    <div className="text-sm">Edge Groups:</div>
                    <EdgeGroupLabels
                      edgeGroupIds={schedule.data?.edgeGroupIds ?? []}
                    />
                  </div>
                  <div className="flex flex-row gap-3 text-sm">
                    <div>Target Version:</div>
                    <div>{schedule.data?.version}</div>
                  </div>
                  <div className="flex flex-row gap-3 text-sm">
                    <div>Scheduled:</div>
                    <div>{formatDate(schedule.data?.scheduledTime ?? '')}</div>
                  </div>
                  <PortainerSelect<StatusType | undefined>
                    isClearable
                    bindToBody
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e ?? undefined)}
                    options={envStatusSelectOptions}
                    data-cy="edge-stacks-environments-status-filter"
                  />
                </div>
              }
            />
          </div>
        </div>
        <div className="w-1/2 col-sm-12 pl-0">
          <EnvironmentDetailsWidget
            title={
              environmentsQuery.environments.find(
                (env) => env.Id === highlightedItem
              )?.Name
            }
            scheduleEnvironment={scheduleEnvironments.find(
              (env) => env.environment.Id === highlightedItem
            )}
          />
        </div>
      </div>
    </div>
  );
}

function renderRow<D extends DefaultType>(
  row: Row<D>,
  highlightedItemId?: string
) {
  const isHighlighted = highlightedItemId === row.id;
  return (
    <TableRow<D>
      cells={row.getVisibleCells()}
      className={clsx(
        isHighlighted &&
          'active !relative !z-[1] !shadow-[0_4px_4px_0_rgba(0,0,0,0.25)]'
      )}
      isHighlighted={isHighlighted}
    />
  );
}
